import React, { useState, useEffect, useCallback } from 'react';
import { useWallet } from '../components/WalletContext';
import { useNavigate } from 'react-router-dom';
import { web, TransactionType } from '@klever/sdk-web';
import {
  FaCoins,
  FaSearch,
  FaFilter,
  FaChevronRight,
  FaSpinner,
  FaInbox,
  FaGift,
  FaChevronDown,
  FaChevronUp,
  FaCheckCircle
} from 'react-icons/fa';
import '../styles/WalletTokensPage.css';

async function fetchAssetInfo(assetId) {
  try {
    const response = await fetch(`https://api.mainnet.klever.org/v1.0/assets/${assetId}`);
    const data = await response.json();
    return {
      precision: data.data.asset.precision,
      logo: data.data.asset.logo || '',
      ticker: data.data.asset.assetTicker || assetId.split('-')[0],
      totalSupply: data.data.asset.totalSupply ? (data.data.asset.totalSupply / Math.pow(10, data.data.asset.precision)).toFixed(2) : 'N/A',
    };
  } catch (err) {
    console.error(`Error fetching asset info for ${assetId}`, err);
    return { precision: 6, logo: '', ticker: assetId.split('-')[0], totalSupply: 'N/A' };
  }
}

const replaceLogoUri = (assetId, logoUrl) => {
  if (assetId === 'WCNFT-25HY') return null;
  if (!logoUrl) return logoUrl;
  if (logoUrl.includes('https://klever-mint.mypinata.cloud')) {
    return logoUrl.replace('https://klever-mint.mypinata.cloud', 'https://ipfs.io');
  }
  return logoUrl;
};

const adjustBalance = (balance, precision) => {
  const adjusted = balance / Math.pow(10, precision);
  return adjusted.toLocaleString('en-US', {
    minimumFractionDigits: Math.min(precision, 2),
    maximumFractionDigits: Math.min(precision, 8)
  });
};

const WalletPageTokens = () => {
  const { walletAddress } = useWallet();
  const [assets, setAssets] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();

  const fetchAssets = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const url = `https://api.mainnet.klever.org/v1.0/address/${walletAddress}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch assets');
      const data = await response.json();

      const assetEntries = Object.entries(data.data.account.assets || {});
      const fungibleTokens = assetEntries.filter(([, assetData]) => assetData.assetType === 0);

      const tokens = await Promise.all(
        fungibleTokens.map(async ([assetId, assetData]) => {
          const info = await fetchAssetInfo(assetId);
          const logoUrl = replaceLogoUri(assetId, info.logo);
          return {
            id: assetId,
            name: assetData.assetName,
            ticker: info.ticker,
            balance: adjustBalance(assetData.balance, assetData.precision),
            frozenBalance: adjustBalance(assetData.frozenBalance || 0, assetData.precision),
            precision: assetData.precision,
            logoUrl,
            totalSupply: info.totalSupply,
          };
        })
      );

      setAssets(tokens);
    } catch (err) {
      console.error('Error fetching tokens:', err);
      setError('Could not load your tokens. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [walletAddress]);

  useEffect(() => {
    if (!walletAddress) return;
    fetchAssets();
  }, [walletAddress, fetchAssets]);

  const handleDetailsClick = (assetId) => navigate(`/wallet/tokens/${assetId}`);

  const filteredAssets = assets.filter((asset) => {
    const matchesSearch = asset.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         asset.ticker.toLowerCase().includes(searchTerm.toLowerCase());
    if (filter === 'Staked') return matchesSearch && parseFloat(asset.frozenBalance) > 0;
    if (filter === 'Non-Zero') return matchesSearch && parseFloat(asset.balance) > 0;
    return matchesSearch;
  });

  const totalTokens = assets.length;
  const stakedCount = assets.filter((a) => parseFloat(a.frozenBalance) > 0).length;

  return (
    <div className="tokens-page">
      <div className="tokens-hero">
        <div className="hero-content">
          <h1 className="hero-title">
            <FaCoins className="hero-icon" />
            Your Tokens
          </h1>
          <p className="hero-subtitle">Manage your fungible tokens on KleverChain</p>
        </div>

        <div className="tokens-stats-bar">
          <div className="token-stat-card">
            <div className="stat-icon-wrapper">
              <FaCoins className="stat-icon" />
            </div>
            <div className="stat-content">
              <span className="stat-label">Total Tokens</span>
              <span className="stat-value">{totalTokens}</span>
            </div>
          </div>
          {stakedCount > 0 && (
            <div className="token-stat-card staked">
              <div className="stat-icon-wrapper">
                <FaGift className="stat-icon" />
              </div>
              <div className="stat-content">
                <span className="stat-label">Staked</span>
                <span className="stat-value">{stakedCount}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="tokens-content">
        <div className="tokens-controls">
          <div className="search-box">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by name or ticker..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="filter-box">
            <FaFilter className="filter-icon" />
            <select className="filter-select" value={filter} onChange={(e) => setFilter(e.target.value)}>
              <option value="All">All Tokens</option>
              <option value="Staked">Staked Only</option>
              <option value="Non-Zero">Non-Zero Balance</option>
            </select>
          </div>
        </div>

        <ClaimAllRewardsSection assets={assets} refreshAssets={fetchAssets} />

        {isLoading ? (
          <div className="tokens-loading">
            <FaSpinner className="loading-spinner" />
            <p className="loading-text">Loading tokens...</p>
          </div>
        ) : error ? (
          <div className="tokens-error">
            <p>{error}</p>
          </div>
        ) : filteredAssets.length === 0 ? (
          <div className="tokens-empty-state">
            <FaInbox className="empty-icon" />
            <h2>No Tokens Found</h2>
            <p>No tokens match your search criteria.</p>
          </div>
        ) : (
          <div className="tokens-grid">
            {filteredAssets.map((asset, index) => (
              <div
                key={asset.id}
                className="token-card"
                style={{ animationDelay: `${Math.min(index * 0.03, 1)}s` }}
              >
                <div className="token-card-header">
                  {asset.logoUrl ? (
                    <img src={asset.logoUrl} alt={asset.name} className="token-logo" />
                  ) : (
                    <div className="token-logo-placeholder">
                      <FaCoins />
                    </div>
                  )}
                </div>

                <div className="token-card-content">
                  <h3 className="token-name">{asset.name}</h3>
                  <p className="token-ticker">{asset.ticker}</p>

                  <div className="token-balances">
                    <div className="balance-item">
                      <span className="balance-label">Balance</span>
                      <span className="balance-value">{asset.balance}</span>
                    </div>
                    {parseFloat(asset.frozenBalance) > 0 && (
                      <div className="balance-item staked">
                        <span className="balance-label">Staked</span>
                        <span className="balance-value">{asset.frozenBalance}</span>
                      </div>
                    )}
                  </div>

                  <button className="token-action-btn" onClick={() => handleDetailsClick(asset.id)}>
                    <span>View Details</span>
                    <FaChevronRight className="action-arrow" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const ClaimAllRewardsSection = ({ assets, refreshAssets }) => {
  const { walletAddress, useKdaFee, selectedKda } = useWallet();
  const [stakedTokens, setStakedTokens] = useState([]);
  const [isLoadingRewards, setIsLoadingRewards] = useState(false);
  const [claimStatus, setClaimStatus] = useState('');
  const [claimState, setClaimState] = useState('');
  const [expandedToken, setExpandedToken] = useState(null);

  const fetchAllRewards = useCallback(async () => {
    setIsLoadingRewards(true);
    try {
      const newStakedTokens = [];
      const stakedAssets = assets.filter((asset) => parseFloat(asset.frozenBalance) > 0);
      for (const asset of stakedAssets) {
        const allowanceUrl = `https://node.mainnet.klever.org/address/${walletAddress}/allowance?asset=${asset.id}`;
        const allowanceRes = await fetch(allowanceUrl);
        const allowanceData = await allowanceRes.json();

        const allRewards = allowanceData.data.allStakingRewards || {};
        const rewardAssets = [];
        for (const [rewardAssetId, rawAmount] of Object.entries(allRewards)) {
          const info = await fetchAssetInfo(rewardAssetId);
          const rewardLogo = replaceLogoUri(rewardAssetId, info.logo);
          const adjusted = parseFloat(rawAmount) / Math.pow(10, info.precision || 6);
          if (adjusted > 0) {
            rewardAssets.push({
              rewardAssetId,
              rewardAmount: adjusted,
              rewardLogo,
            });
          }
        }
        if (rewardAssets.length > 0) {
          newStakedTokens.push({
            tokenId: asset.id,
            tokenName: asset.name,
            tokenLogo: asset.logoUrl,
            rewardAssets,
          });
        }
      }
      setStakedTokens(newStakedTokens);
    } catch (err) {
      console.error('Error fetching all rewards:', err);
    } finally {
      setIsLoadingRewards(false);
    }
  }, [walletAddress, assets]);

  useEffect(() => {
    if (!walletAddress || assets.length === 0) return;
    fetchAllRewards();
  }, [walletAddress, assets, fetchAllRewards]);

  const handleClaimAll = async () => {
    setClaimStatus('');
    setClaimState('');
    if (stakedTokens.length === 0) {
      setClaimState('error');
      setClaimStatus('No rewards to claim.');
      setTimeout(() => { setClaimStatus(''); setClaimState(''); }, 3000);
      return;
    }
    try {
      setClaimState('processing');
      setClaimStatus('Processing claim...');

      const actions = stakedTokens.map((st) => ({
        payload: { claimType: 0, id: st.tokenId },
        type: TransactionType.Claim,
      }));
      const txOptions = useKdaFee && selectedKda ? { kdaFee: selectedKda } : undefined;
      const unsignedTx = await web.buildTransaction(actions, undefined, txOptions);
      const signedTx = await web.signTransaction(unsignedTx);

      if (!signedTx) throw new Error('Transaction cancelled by user.');
      await web.broadcastTransactions([signedTx]);

      setClaimState('success');
      setClaimStatus('Rewards claimed successfully!');
      
      setTimeout(async () => {
        await Promise.all([fetchAllRewards(), refreshAssets()]);
        setClaimStatus('');
        setClaimState('');
      }, 2500);
    } catch (error) {
      console.error('Claim All error:', error);
      setClaimState('error');
      setClaimStatus(error.message.toLowerCase().includes('cancel') 
        ? 'Transaction cancelled.' 
        : 'Claim failed. Please try again.');
      setTimeout(() => { setClaimStatus(''); setClaimState(''); }, 3000);
    }
  };

  const toggleExpand = (tokenId) => setExpandedToken(expandedToken === tokenId ? null : tokenId);

  if (stakedTokens.length === 0 && !isLoadingRewards) return null;

  return (
    <div className="rewards-section">
      <div className="rewards-header">
        <div className="rewards-title-wrapper">
          <FaGift className="rewards-icon" />
          <h2 className="rewards-title">Staking Rewards</h2>
        </div>
      </div>

      {isLoadingRewards ? (
        <div className="rewards-loading">
          <FaSpinner className="loading-spinner" />
        </div>
      ) : stakedTokens.length > 0 ? (
        <div className="rewards-content">
          <p className="rewards-description">
            You have rewards from <strong>{stakedTokens.length}</strong> token{stakedTokens.length !== 1 ? 's' : ''}
          </p>

          <div className="rewards-list">
            {stakedTokens.map((st) => (
              <div key={st.tokenId} className="reward-token-card">
                <div className="reward-token-header" onClick={() => toggleExpand(st.tokenId)}>
                  <div className="reward-token-info">
                    {st.tokenLogo ? (
                      <img src={st.tokenLogo} alt={st.tokenName} className="reward-token-logo" />
                    ) : (
                      <div className="reward-token-logo-placeholder">
                        <FaCoins />
                      </div>
                    )}
                    <span className="reward-token-name">{st.tokenName}</span>
                  </div>
                  {expandedToken === st.tokenId ? <FaChevronUp /> : <FaChevronDown />}
                </div>

                {expandedToken === st.tokenId && (
                  <div className="reward-details">
                    {st.rewardAssets.map((ra) => (
                      <div key={ra.rewardAssetId} className="reward-item">
                        {ra.rewardLogo ? (
                          <img src={ra.rewardLogo} alt="Reward" className="reward-item-logo" />
                        ) : (
                          <div className="reward-item-logo-placeholder">
                            <FaCoins />
                          </div>
                        )}
                        <span className="reward-item-id">{ra.rewardAssetId}</span>
                        <span className="reward-item-amount">{ra.rewardAmount.toFixed(4)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <button
            className="claim-all-btn"
            onClick={handleClaimAll}
            disabled={claimState === 'processing'}
          >
            {claimState === 'processing' ? (
              <>
                <FaSpinner className="btn-spinner" />
                <span>Claiming...</span>
              </>
            ) : (
              <>
                <FaGift />
                <span>Claim All Rewards</span>
              </>
            )}
          </button>

          {claimStatus && (
            <div className={`claim-status-box ${claimState}`}>
              {claimState === 'processing' && <FaSpinner className="status-spinner" />}
              {claimState === 'success' && <FaCheckCircle />}
              <span>{claimStatus}</span>
            </div>
          )}
        </div>
      ) : (
        <p className="rewards-empty">No rewards available at this time.</p>
      )}
    </div>
  );
};

export default WalletPageTokens;